import React, { useEffect } from "react";
import { useStoreState } from "pullstate";
import classnames from "classnames";

import { PodcastsInterface } from "@/helpers/pageHelpers/Podcast/interface";
import { defaultPageItems } from "@/helpers/apiPathHelpers";
import { apiPath } from "@/helpers/apiPath";
import { apiClient } from "@/helpers/apiClient";
import PageWrapper from "@/components/PageWrapper";
import Container from "@/components/Container";
import MetaHead from "@/components/MetaHead";
import Button from "@/components/Button";
import PodcastTiles from "@/containers/PodcastTiles";
import ContentLoader from "@/components/ContentLoader";
import PodcastTopAd from "@/components/Advertisement/PodcastTop";
import { Schema } from "@/components/MetaHead/Head.interface";
import { UIStore } from "@/store/UIStore";
import ListHeader from "@/components/ListHeader";
import { podcastMetaData } from "@/helpers/pageMetaData";
import { usePagination } from "@/helpers/usePagination";
import { useFilters } from "@/helpers/useFilters";
import Filters from "@/components/Filters";
import SuggestedPodcasts from "@/components/EmptyState/SuggestedPodcasts";
import LatestSection from "@/components/LatestSection";
import { FiltersVariant } from "@/components/Filters/Filters.interface";

import styles from "./Podcasts.module.scss";

const sortOptions = [
  {
    name: "Od najnowszych serii",
    value: "published_at-desc",
  },
  {
    name: "A - Z",
    value: "title-asc",
  },
  {
    name: "Z - A",
    value: "title-desc",
  },
  {
    name: "Od najpopularniejszych",
    value: "popularity-desc",
  },
];

export const Podcasty: React.FC<PodcastsInterface> = ({
  seo,
  podcasts,
  latestEpisodes,
}) => {
  const metaData = {
    ...seo,
    ...podcastMetaData,
  };
  const { handleSearchChange, handleSortChange, sort, debouncedQuery } =
    useFilters();

  const pageData = { headerLabel: "Podcasty" };
  const {
    mutate,
    elements: podcastsList,
    isLoadingMore,
    isLoadingInitialData,
    isReachingEnd,
    isEmpty,
    loadMore,
  } = usePagination(podcasts, defaultPageItems.PODCASTS, (index) =>
    apiPath.PODCASTS({ page: index + 1, searchQuery: debouncedQuery, sort })
  );

  const schemaData = {
    type: Schema.Page,
    title: "Podcasty",
    slug: "podcasty",
  };
  const { userLoggedIn } = useStoreState(UIStore, (store) => store);

  useEffect(() => {
    mutate();
  }, [userLoggedIn]);

  return (
    <PageWrapper pageData={pageData} className={styles.container}>
      <MetaHead metaData={metaData} schemaData={schemaData} />

      <div className={styles.podcastTopAd}>
        <Container>
          <PodcastTopAd />
        </Container>
      </div>
      <LatestSection episodes={latestEpisodes} />
      <ListHeader
        title="Wszystkie podcasty"
        className={styles.filters}
        component="h1"
      >
        <Filters
          handleSearchChange={handleSearchChange}
          handleDropdownChange={handleSortChange}
          sortOptions={sortOptions}
          placeholder="Szukaj wśród podcastów"
          variant={FiltersVariant.Sort}
        />
      </ListHeader>

      <Container>
        <div className={classnames(styles.list, isEmpty && styles.empty)}>
          <PodcastTiles items={podcastsList} searchQuery={debouncedQuery} />

          {isLoadingMore && <ContentLoader height={150} amount={3} />}

          {!isReachingEnd && !isLoadingInitialData && (
            <Button
              variant="secondary"
              onClick={loadMore}
              className={styles.loadMore}
            >
              {isLoadingMore ? "Wczytuję..." : "Pokaż więcej podcastów"}
            </Button>
          )}
        </div>
      </Container>
      {isEmpty && <SuggestedPodcasts />}
    </PageWrapper>
  );
};

export async function getStaticProps() {
  const client = apiClient();
  const { data: seo } = await client.get(apiPath.SEO);
  const { data } = await client.get(apiPath.PODCASTS({ page: 1 }));
  const { data: latestEpisodes } = await client.get(
    apiPath.EPISODE_LIST({ perPage: 7 })
  );

  return {
    props: {
      seo,
      podcasts: data.items,
      latestEpisodes,
    },
    revalidate: 86400,
  };
}

export default Podcasty;
